<template>
  <div v-loading="loading">
    <el-button
      type="primary"
      size="small"
      icon="fas fa-download"
      style="margin-bottom: 20px"
      @click="generatePng()"
    >
      Выгрузить в PNG
    </el-button>
    <div ref="pngContent" class="png-content" v-if="!loading">
      <div class="user-info">
        <p v-if="scoreInfo.user" class="name">{{ scoreInfo.user.full_name }}</p>
        <p v-if="scoreInfo.user"><b>Должность:</b> {{ scoreInfo.user.position }}</p>
        <p v-if="scoreInfo.user"><b>Подразделение:</b> {{ scoreInfo.user.subdivision }}</p>
        <p v-if="scoreInfo.user"><b>Компания:</b> {{ scoreInfo.user.company }}</p>
        <p v-if="scoreInfo.profile"><b>Профиль:</b> {{ scoreInfo.profile.name }}</p>
        <p v-if="scoreInfo.evaluation"><b>Оценка:</b> {{ scoreInfo.evaluation.name }}</p>
        <p v-if="scoreInfo.evaluation"><b>Менеджер оценки:</b> {{ scoreInfo.evaluation.creator.full_name }}</p>
      </div>
      <div class="chart">
        <p class="title">Радар компетенций</p>
        <p class="desc">Диаграмма показывает оценки по всем компетенциям от всех групп респондентов.
          Она поможет выявить расхождения между оценками от разных групп.</p>
        <div class="chart-block">
          <div v-if="scoreInfo.competencies && scoreInfo.competencies.length">
            <div class="chart-container">
              <highcharts ref="chart" :options="chartOptions"></highcharts>
            </div>
          </div>
          <p v-else class="block-empty">
            <i class="fas fa-info-circle"></i>
            {{ scoreInfo.setting.show_result_char || teacher ? 'Недостаточно данных для построения графика' : 'Данный отчет заблокирован' }}
          </p>
        </div>
      </div>
      <div class="scoreInfo">
        <p class="title">Средние оценки</p>
        <p class="desc">Представлены данные по средним оценкам всех групп респондентов.</p>
        <div class="scoreInfo-block">
          <div v-if="scoreInfo.average_scores_by_type && Object.keys(scoreInfo.average_scores_by_type).length">
            <div class="table-container">
              <div class="table-container-header">
                <div>Группа</div>
                <div>Оценка</div>
              </div>
              <div v-if="scoreInfo.average_score > 0" class="table-container-body">
                <div>Средний балл</div>
                <div>{{ scoreInfo.average_score }}</div>
              </div>
              <div v-if="scoreInfo.average_scores_by_type.self_score" class="table-container-body">
                <div>Самооценка</div>
                <div>{{ scoreInfo.average_scores_by_type.self_score }}</div>
              </div>
              <div v-if="scoreInfo.average_scores_by_type.colleague_score" class="table-container-body">
                <div>Оценка коллег</div>
                <div>{{ scoreInfo.average_scores_by_type.colleague_score }}</div>
              </div>
              <div v-if="scoreInfo.average_scores_by_type.chief_score" class="table-container-body">
                <div>Оценка руководителя</div>
                <div>{{ scoreInfo.average_scores_by_type.chief_score }}</div>
              </div>
              <div v-if="scoreInfo.average_scores_by_type.subordinate_score" class="table-container-body">
                <div>Оценка подчиненных</div>
                <div>{{ scoreInfo.average_scores_by_type.subordinate_score }}</div>
              </div>
            </div>
          </div>
          <p v-else class="block-empty">
            <i class="fas fa-info-circle"></i>
            {{ scoreInfo.setting.show_main_result || teacher ? 'Недостаточно данных для вывода средних оценок' : 'Данный отчет заблокирован' }}
          </p>
        </div>
      </div>
      <div class="competencies">
        <p class="title">Сводка по компетенциям</p>
        <p class="desc">В таблице приведены оценки по каждой из компетенций для каждой роли респондентов.</p>
        <div class="competencies-block">
          <el-table v-if="scoreInfo.competence_results && scoreInfo.competence_results.length" :data="scoreInfo.competence_results" class="competence-table">
            <el-table-column prop="name" label="Компетенция"></el-table-column>
            <el-table-column width="160" prop="target_score" label="Целевое значение"></el-table-column>
            <el-table-column width="120" prop="main_score" label="Средний балл">
              <template v-slot="scope">
                <div v-if="scope.row.main_score" :class="scope.row.main_score >= scope.row.target_score ? 'green' : 'orange'">
                  {{ scope.row.main_score }}
                </div>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column width="120" label="Самооценка">
              <template v-slot="scope">
                <div v-if="scope.row.self_score" :class="scope.row.self_score >= scope.row.target_score ? 'green' : 'orange'">
                  {{ scope.row.self_score }}
                </div>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column width="120" label="Коллеги">
              <template v-slot="scope">
                <div v-if="scope.row.colleague_score" :class="scope.row.colleague_score >= scope.row.target_score ? 'green' : 'orange'">
                  {{ scope.row.colleague_score }}
                </div>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column width="120" label="Руководитель">
              <template v-slot="scope">
                <div v-if="scope.row.chief_score" :class="scope.row.chief_score >= scope.row.target_score ? 'green' : 'orange'">
                  {{ scope.row.chief_score }}
                </div>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column width="120" label="Подчиненные">
              <template v-slot="scope">
                <div v-if="scope.row.subordinate_score" :class="scope.row.subordinate_score >= scope.row.target_score ? 'green' : 'orange'">
                  {{ scope.row.subordinate_score }}
                </div>
                <span v-else>-</span>
              </template>
            </el-table-column>
          </el-table>
          <p v-else class="block-empty">
            <i class="fas fa-info-circle"></i>
            {{ scoreInfo.setting.show_result_competencies || teacher ? 'Недостаточно данных для сводки компетенций' : 'Данный отчет заблокирован' }}
          </p>
        </div>
      </div>
      <div class="recommendations">
        <p class="title">Рекоммендации</p>
        <p class="desc">В таблице приведены рекомендации по компетенциям.</p>
        <div class="recommendations-block">
          <div v-if="scoreInfo.competence_recommendations && scoreInfo.competence_recommendations.length" class="table-container-container">
            <div class="table-container-header">
              <div style="width: 40%;">Компетенция</div>
              <div style="width: 60%;">Рекомендации</div>
            </div>
            <div class="table-container-body" v-for="competence_recommendation in scoreInfo.competence_recommendations" >
              <div style="width: 40%;">{{ competence_recommendation.name }}</div>
              <div style="width: 60%;">
                <p v-for="recommendation in competence_recommendation.recommendations" style="width: 100%">
                  <b>{{ recommendation.name }}</b><br>
                  {{ recommendation.description }}
                </p>
              </div>
            </div>
          </div>
          <p v-else class="block-empty">
            <i class="fas fa-info-circle"></i>
            {{ scoreInfo.setting.show_result_recommendations || teacher ? 'Недостаточно данных для вывода рекомендаций' : 'Данный отчет заблокирован' }}
          </p>
        </div>
      </div>
      <div class="strong_sides">
        <p class="title">Сильные стороны</p>
        <p class="desc">Компетенции, которыми оцениваемый владеет на высоком уровне, в этом уверены его коллеги и он сам.</p>
        <div class="strong_sides-block">
          <div v-if="scoreInfo.strong_sides && scoreInfo.strong_sides.length" class="table-container-container">
            <div class="table-container-header">
              <div style="width: 40%;">Компетенция</div>
              <div style="width: 60%;">Описание</div>
            </div>
            <div class="table-container-body" v-for="strong_side in scoreInfo.strong_sides" >
              <div style="width: 40%;">{{ strong_side.name }}</div>
              <div style="width: 60%;">{{ strong_side.description }}</div>
            </div>
          </div>
          <p v-else class="block-empty">
            <i class="fas fa-info-circle"></i>
            {{ scoreInfo.setting.show_result_strong_sides || teacher ? 'Недостаточно данных для вывода сильных сторон' : 'Данный отчет заблокирован' }}
          </p>
        </div>
      </div>
      <div class="comments">
        <p class="title">Комментарии</p>
        <p class="desc">Комментарии к индикаторам, которые оценивающие оставляли в момент оценки.</p>
        <div class="comments-block">
          <div v-if="scoreInfo.indicators_comments && Object.keys(scoreInfo.indicators_comments).length" class="table-container-container">
            <div class="table-container-header">
              <div style="width: 20%">Компетенция</div>
              <div style="width: 20%">Индикаторы</div>
              <div style="width: 60%">Комментарии</div>
            </div>
            <div class="table-container-body" v-for="indicator_comments in scoreInfo.indicators_comments">
              <div style="width: 20%">{{ indicator_comments.competence_name }}</div>
              <div style="width: 20%">{{ indicator_comments.indicator_name }}</div>
              <div style="width: 60%">
                <p v-for="comment in indicator_comments.comments" style="width: 100%"><i class="fas fa-comment"></i> {{ comment }}</p>
              </div>
            </div>
          </div>
          <p v-else class="block-empty">
            <i class="fas fa-info-circle"></i>
            {{ scoreInfo.setting.show_result_comments || teacher ? 'Недостаточно данных для вывода комментариев' : 'Данный отчет заблокирован' }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'
import HighchartsMore from 'highcharts/highcharts-more'
import HighchartsExporting from 'highcharts/modules/exporting'

HighchartsMore(Highcharts)
HighchartsExporting(Highcharts)

export default {
  name: "my-evaluation-result",
  components: { highcharts: Chart },
  data() {
    return {
      tab: 'chart',
      loading: true,
      chartOptions: {},
      scoreInfo: {},
    }
  },
  props: {
    teacher: { type: Boolean, default: false },
    userId: { type: Number, required: true },
    evaluationId: { type: Number, required: true },
  },
  mounted() {
    this.loadEvaluationResult();
  },
  methods: {
    generatePng() {
      const element = this.$refs.pngContent
      import('html2canvas').then(html2canvas => {
        html2canvas.default(element, {
          scale: 2,
          useCORS: true,
          scrollY: 0
        }).then(originalCanvas => {
          const padding = 40;
          const canvas = document.createElement('canvas');
          canvas.width = originalCanvas.width + padding * 2;
          canvas.height = originalCanvas.height + padding * 2;
          const ctx = canvas.getContext('2d');

          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(originalCanvas, padding, padding);

          const link = document.createElement('a');
          link.download = 'Персональный отчет по оценке пользователя ' + this.scoreInfo.user.full_name + '.png';
          link.href = canvas.toDataURL('image/png');
          link.click();
        })
      })
    },
    loadEvaluationResult() {
      this.loading = true;
      this.tab = 'chart';
      (this.teacher ? this.$api.evaluation.evalEvaluationGetResult : this.$api.evaluation.myEvaluationGetResult)({
        user_id: this.userId,
        evaluation_id: this.evaluationId,
      })
        .then(data => {
          this.loading = false;
          this.scoreInfo = data;

          const allSeries = [
            {
              name: 'Самооценка',
              data: data.scores.self_score ?? [],
              pointPlacement: 'on',
              lineWidth: 2,
              marker: { enabled: false },
            },
            {
              name: 'Оценка коллег',
              data: data.scores.colleague_score ?? [],
              pointPlacement: 'on',
              lineWidth: 2,
              marker: { enabled: false },
            },
            {
              name: 'Оценка руководителя',
              data: data.scores.chief_score ?? [],
              pointPlacement: 'on',
              lineWidth: 2,
              marker: { enabled: false },
            },
            {
              name: 'Оценка подчиненных',
              data: data.scores.subordinate_score ?? [],
              pointPlacement: 'on',
              lineWidth: 2,
              marker: { enabled: false },
            },
            {
              name: 'Общая оценка',
              data: data.scores.main_score ?? [],
              pointPlacement: 'on',
              lineWidth: 2,
              marker: { enabled: false },
            }
          ];

          const filteredSeries = allSeries.filter(s => Array.isArray(s.data) && s.data.length > 0);

          this.chartOptions = {
            credits: {
              enabled: false,
            },
            exporting: {
              enabled: false,
            },
            chart: {
              polar: true,
              type: 'line',
              height: 500,
            },
            title: {
              text: 'Оценка по компетенциям',
            },
            pane: {
              size: '90%',
            },
            xAxis: {
              categories: data.competencies,
              tickmarkPlacement: 'on',
              lineWidth: 0,
              labels: {
                useHTML: true,
                formatter: function () {
                  const text = this.value;
                  const maxLength = 100;
                  const displayText = text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
                  return `<span title="${text}">${displayText}</span>`;
                },
                style: {
                  textAlign: 'center'
                }
              }
            },
            yAxis: {
              gridLineInterpolation: 'polygon',
              lineWidth: 0,
              min: 1,
              max: data.max_score,
              tickInterval: 1,
              title: {
                text: 'Оценка',
                style: {
                  color: 'transparent'
                }
              }
            },
            tooltip: {
              shared: true,
            },
            series: filteredSeries,
          };
        });
    },
  }
}
</script>

<style>
  .legend-button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    margin: 4px;
    border-radius: 6px;
    font-weight: bold;
    cursor: pointer;
    transition: 0.3s;
    border: 2px solid #e6e6e7;
  }

  .legend-button .legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-block;
  }

  .legend-button.inactive {
    opacity: 0.5;
    filter: grayscale(0.8);
  }

  .result .el-card {
    border: none;
  }

  .chart-container {
    height: 500px;
    max-width: 1000px;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .user-info .name {
    font-size: 25px;
    margin: 0 0 20px 0;
    font-weight: bold;
  }

  .chart,
  .scoreInfo,
  .comments,
  .competencies,
  .recommendations,
  .strong_sides {
    margin-top: 50px;
  }

  .title {
    font-weight: bold;
    font-size: 20px;
  }

  .desc {
    font-size: 14px;
    color: #6b7074;
    margin-bottom: 35px;
  }

  .table-container {
    margin-top: 20px;
  }

  .comments {
    margin-bottom: 50px !important;
  }

  .table-container-header {
    font-size: 15px;
    color: #6b7074;
    padding: 0 0 0 10px;
    border-bottom: 2px solid #e6e6e7;
    display: flex;
    justify-content: space-between;
  }

  .table-container-header div,
  .table-container-body div {
    padding: 15px 5px;
  }

  .table-container-body {
    font-weight: bold;
    font-size: 18px;
    border-bottom: 2px solid #e6e6e7;
    border-left: 5px solid #f37363;
    display: flex;
    justify-content: space-between;
  }

  .comments .table-container-body div {
    border-right: 2px solid #e6e6e7;
  }

  .strong_sides .table-container-body,
  .recommendations .table-container-body {
    font-size: 16px !important;
  }

  .scoreInfo-block .table-container-header div:first-child,
  .scoreInfo-block .table-container-body div:first-child {
    width: 70%;
  }

  .scoreInfo-block .table-container-header div:last-child,
  .scoreInfo-block .table-container-body div:last-child {
    width: 30%;
  }

  .strong_sides .table-container-body div:last-child,
  .recommendations .table-container-body div:last-child,
  .comments .table-container-body div:last-child {
    font-weight: normal !important;
  }

  .green {
    background-color: #dff3e8;
    padding: 5px;
  }

  .orange {
    background-color: #fef6d8;
    padding: 5px;
  }

  .competence-table table .cell {
    font-weight: bold;
  }

  .block-empty {
    border: 2px solid #e6e6e7;
    padding: 30px;
    border-radius: 10px;
    text-align: center;
    color: #6b7074;
    font-weight: bold;
  }
</style>
